import * as React from 'react';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { IWidget } from '../../../../pdc-platform/src/ts/Interfaces';
import { WidgetData } from '../../ts/WidgetData';
import { IValuesCollection } from '../../ts/appTheme';
import { IDataEditValues } from '../dataEdit/Interfaces';
import { WidgetTypeSelect } from '../../../../pdc-platform/src/tsx/WidgetTypeSelect';
import { ParameterField } from '../ParameterField';
import UriField from '../UriField';
import { ColorBoxField } from '../ColorBoxField';
import WidgetParametersEdit from '../../../../pdc-platform/src/tsx/WidgetParametersEdit';
import { PasteButton } from '../PasteTargetDialog';
import ClipboardCopy from '../../../../pdc-platform/src/tsx/ClipboardCopy';
import * as Utilities from '../../ts/Utilities';
import CKEditField from '../CKEditField';
import NumberField from '../NumberField';
import BooleanField from '../BooleanField';

export interface IMultiWidgetFieldWidgetDataOptions {
    headerTemplate?: string;
    iconHeight?: number;
    activateByDefault?: boolean;
}

export interface IHashItem {
    data: WidgetData<IMultiWidgetFieldWidgetDataOptions>;
    value: IWidget<IMultiWidgetFieldWidgetDataOptions>;
}

export interface IHashCollection {
    [id: number]: IHashItem;
}

type TabType = 'main' | 'style' | 'headerTemplate';

export interface ITabCommonProperties {
    obj: IHashItem;
    glob: IGlob;
}

export interface IMultiWidgetFieldParamOptions {
    values;
    inHtmlContainer?: boolean;
}

export interface IMultiWidgetFieldValues {
    [tabsFieldName: string]: any;
}

export interface IStyleTabProperties extends ITabCommonProperties {
    values: IMultiWidgetFieldValues;
    showTabIcons: boolean;
    showTabBackgroundColor: boolean;
    defaultTabBackgroundColor: string;
    showTabTextColors: boolean;
    showActivateByDefault: boolean;
    defaultTabTextColor: string;
    parentTheme: IValuesCollection;
    tabsFieldName: string;
    onChangeIcon(obj: IHashItem, value: string): void;
    onChangeIconHeight(obj: IHashItem, value: number): void;
    onChangeBackColor(obj: IHashItem, value: string): void;
    onChangeTextColor(obj: IHashItem, value: string): void;
    onChangeActivateByDefault(obj: IHashItem, value: boolean): void;
}

export interface IHeaderTemplateTabProperties extends ITabCommonProperties {
    onHeaderTemplateChange(obj: IHashItem, value: string): void;
}

export interface IMultiWidgetFieldTabProperties extends IStyleTabProperties, IHeaderTemplateTabProperties {
    parameterProps: any[];
    paramOptions: IMultiWidgetFieldParamOptions;
    tabID: number;
    disabledTypes: string[];
    hideType: boolean;
    hideTabs: string[];
    selectedInternalTab: number;
    showHeaderTemplate: boolean;
    onSetWidgetType(key: number, moduleName: string): void;
    onSaveInCollectionClick(): void;
    onClipboardCopyClick(): void;
    onEditJSONClick(): void;
    onSelectFromCollectionClick(): void;
    onPaste(value: string): void;
    onSetFieldValues(id: number, type: string, values: IDataEditValues): void;
    onInternalWidgetChangeTab(id: number, type: string, i: number): void;
}

export function MultiWidgetFieldTab(props: IMultiWidgetFieldTabProperties) {
    const [activeTab, setActiveTab] = React.useState<TabType>('main');

    const { obj, glob } = props;
    const tabContent = [];
    const showStyleTab = props.showTabIcons || props.showTabBackgroundColor || props.showTabTextColors;

    if (activeTab === 'style') {
        tabContent.push(
            <StyleTab
                key="style-tab"
                obj={obj}
                parentTheme={props.parentTheme}
                values={props.values}
                showTabIcons={props.showTabIcons}
                showTabBackgroundColor={props.showTabBackgroundColor}
                defaultTabBackgroundColor={props.defaultTabBackgroundColor}
                showTabTextColors={props.showTabTextColors}
                defaultTabTextColor={props.defaultTabTextColor}
                showActivateByDefault={props.showActivateByDefault}
                tabsFieldName={props.tabsFieldName}
                glob={glob}
                onChangeIcon={props.onChangeIcon}
                onChangeIconHeight={props.onChangeIconHeight}
                onChangeBackColor={props.onChangeBackColor}
                onChangeTextColor={props.onChangeTextColor}
                onChangeActivateByDefault={props.onChangeActivateByDefault}
            />
        );
    } else if (activeTab === 'headerTemplate') {
        tabContent.push(
            <HeaderTemplateTab
                key="header-template-tab"
                obj={obj}
                glob={glob}
                onHeaderTemplateChange={props.onHeaderTemplateChange}
            />
        );
    } else {
        if (props.hideType !== true) {
            tabContent.push(
                <WidgetTypeSelect
                    id={obj.data.id}
                    key={tabContent.length}
                    type={obj.data.type}
                    //data={ obj.data }
                    disabledTypes={props.disabledTypes ?? []}
                    glob={glob}
                    getDialogContainer={getDialogContainer}
                    onSetWidgetType={props.onSetWidgetType}
                />
            );

            let saveInCollectionButton: React.JSX.Element;
            let clipboardCopyButton: React.JSX.Element;
            let editJSONButton: React.JSX.Element;

            if (obj.data.cid != null && obj.data.widget != null && !obj.data.widget.isInvalid) {
                saveInCollectionButton = (
                    <button className="btn btn-default btn-save-in-collection" onClick={props.onSaveInCollectionClick}>
                        {'Save In Collection'}
                    </button>
                );

                const widgetCopy = $.extend(true, {}, obj.data.getDataForSave());

                Utilities.jsonNormalize_DataSourceIDToName(widgetCopy, glob);
                Utilities.jsonNormalize_ThemeIDToName(widgetCopy, glob);

                if (!props.paramOptions || !props.paramOptions.inHtmlContainer) {
                    clipboardCopyButton = (
                        <ClipboardCopy
                            isButtonWithGlyph={false}
                            text={JSON.stringify(widgetCopy, null, '\t')}
                            caption={'Copy to Clipboard'}
                            onClick={props.onClipboardCopyClick}
                        />
                    );
                }
            }

            if (props.paramOptions?.inHtmlContainer) {
                editJSONButton = (
                    <button className="btn btn-default btn-paste-from-clipboard" onClick={props.onEditJSONClick}>
                        <span className="glyphicon glyphicon-edit" />
                        {'Edit JSON'}
                    </button>
                );
            }

            tabContent.push(
                <div key="service-buttons" className="btn-group ml-2" role="group" aria-label="Service Buttons">
                    <button
                        className="btn btn-default btn-add-from-collection"
                        onClick={props.onSelectFromCollectionClick}
                    >
                        {'Select from Collection'}
                    </button>

                    {saveInCollectionButton}

                    {clipboardCopyButton}

                    <PasteButton theme={props.parentTheme} glob={glob} onPaste={props.onPaste} />

                    {editJSONButton}
                </div>
            );
        }

        const parameterProps = $.extend(
            true,
            {
                tabID: props.tabID
            },
            props.parameterProps
        );

        const paramOptions = $.extend(
            true,
            {
                tabID: props.tabID
            },
            props.paramOptions
        );

        tabContent.push(
            <WidgetParametersEdit
                id={obj.data.id}
                key={tabContent.length}
                type={obj.data.type}
                data={obj.data.widget}
                loading={obj.data.loading}
                parameterProps={parameterProps}
                paramOptions={paramOptions}
                hideTabs={props.hideTabs}
                selectedInternalTab={props.selectedInternalTab}
                glob={glob}
                //hideLocked={ true }
                onSetFieldValues={props.onSetFieldValues}
                onChangeTab={props.onInternalWidgetChangeTab}
            />
        );
    }

    const getTab = (tabType: TabType, caption: string) => {
        return (
            <li className="nav-item">
                <a
                    href="#"
                    className={'nav-link' + (activeTab === tabType ? ' active' : '')}
                    onClick={(e) => {
                        e.preventDefault();
                        setActiveTab(tabType);
                    }}
                >
                    {caption}
                </a>
            </li>
        );
    };

    return (
        <>
            <ul className="nav nav-tabs widget-tabs">
                {getTab('main', 'Main')}
                {showStyleTab ? getTab('style', 'Style') : null}
                {props.showHeaderTemplate ? getTab('headerTemplate', 'Header Template') : null}
            </ul>
            <div className="tab-content">{tabContent}</div>
        </>
    );
}

function StyleTab(props: IStyleTabProperties) {
    const { obj, glob } = props;
    const tabsFieldName = props.tabsFieldName ?? 'tabs';

    let tabs;

    if (props.values != null && props.values[tabsFieldName] != null) {
        tabs = props.values[tabsFieldName];
    }

    let tabBackgroundColorElement: React.JSX.Element;

    if (props.showTabBackgroundColor) {
        let tabBackColor = obj.value.tabBackColor;

        if (tabs != null && tabs[obj.value.id] != null) {
            tabBackColor = tabs[obj.value.id].tabBackColor;
        }

        tabBackgroundColorElement = (
            <ColorParameterField
                value={tabBackColor}
                title={'Tab Background Color'}
                theme={props.parentTheme}
                defaultColor={props.defaultTabBackgroundColor}
                glob={glob}
                onClear={() => {
                    props.onChangeBackColor(obj, null);
                }}
                onChange={(value) => {
                    props.onChangeBackColor(obj, value);
                }}
            />
        );
    }

    let tabTextColorElement: React.JSX.Element;

    if (props.showTabTextColors) {
        let tabTextColor = obj.value.tabTextColor;

        if (tabs != null && tabs[obj.value.id] != null) {
            tabTextColor = tabs[obj.value.id].tabTextColor;
        }

        tabTextColorElement = (
            <ColorParameterField
                value={tabTextColor}
                title={'Tab Text Color'}
                theme={props.parentTheme}
                defaultColor={props.defaultTabTextColor}
                glob={glob}
                onClear={() => {
                    props.onChangeTextColor(obj, null);
                }}
                onChange={(value) => {
                    props.onChangeTextColor(obj, value);
                }}
            />
        );
    }

    let colorCard: React.JSX.Element;

    if (tabBackgroundColorElement != null || tabTextColorElement != null) {
        colorCard = (
            <div className="card mt-1">
                <div className="card-body p-1">
                    <div className="d-flex">
                        {tabBackgroundColorElement}
                        {tabTextColorElement}
                    </div>
                </div>
            </div>
        );
    }

    let iconCard: React.JSX.Element;

    if (props.showTabIcons) {
        let tabIcon = obj.value.icon;

        if (tabs != null && tabs[obj.value.id] != null) {
            tabIcon = tabs[obj.value.id].icon;
        }

        iconCard = (
            <div className="card mt-1">
                <div className="card-body p-1">
                    <div className="d-flex">
                        <IconParameterField
                            value={tabIcon}
                            glob={glob}
                            onClear={() => {
                                props.onChangeIcon(obj, null);
                            }}
                            onChange={(value) => {
                                props.onChangeIcon(obj, value);
                            }}
                        />
                        <IconHeightParameterField
                            value={obj.value.options?.iconHeight}
                            onClear={() => {
                                props.onChangeIconHeight(obj, null);
                            }}
                            onChange={(value) => {
                                props.onChangeIconHeight(obj, value);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    let activateByDefaultCard: React.JSX.Element;

    if (props.showActivateByDefault) {
        activateByDefaultCard = (
            <div className="card mt-1">
                <div className="card-body p-1">
                    <div className="d-flex">
                        <ActivateByDefaultParameterField
                            value={obj.value.options?.activateByDefault}
                            onChange={(value) => {
                                props.onChangeActivateByDefault(obj, value);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {colorCard} {iconCard}
            {activateByDefaultCard}
        </>
    );
}

function ColorParameterField(props: {
    value: string;
    title: string;
    theme: IValuesCollection;
    defaultColor: string;
    glob: IGlob;
    onClear(): void;
    onChange(value: string): void;
}) {
    const showInheritedIcon = props.defaultColor != null && props.value == null;

    return (
        <div className="flex-fill">
            <ParameterField
                title={props.title}
                clearable={true}
                showClearIcon={props.value != null}
                showInheritedIcon={showInheritedIcon}
                onClear={props.onClear}
            >
                <ColorBoxField
                    value={props.value ?? props.defaultColor}
                    themeObj={props.theme}
                    hideTransparency={false}
                    showText={true}
                    readOnly={false}
                    disabled={false}
                    glob={props.glob}
                    onChange={(value: string) => {
                        if (value !== props.defaultColor) {
                            props.onChange(value);
                        } else {
                            props.onChange(undefined);
                        }
                    }}
                />
            </ParameterField>
        </div>
    );
}

function IconParameterField(props: { value: string; glob: IGlob; onClear(): void; onChange(value: string): void }) {
    return (
        <div className="flex-fill">
            <ParameterField
                title={'Tab Icon'}
                clearable={true}
                showClearIcon={props.value != null}
                onClear={props.onClear}
            >
                <div style={{ width: '12rem' }}>
                    <UriField
                        value={props.value}
                        inline={true}
                        canBrowse={true}
                        browseInstanceId={'tabbed-widget-resources'}
                        delayChangeInterval={500}
                        glob={props.glob}
                        onChange={props.onChange}
                    />
                </div>
            </ParameterField>
        </div>
    );
}

function IconHeightParameterField(props: { value: number; onClear(): void; onChange(value: number): void }) {
    return (
        <div className="flex-fill">
            <ParameterField
                title={'Icon Height'}
                clearable={true}
                showClearIcon={props.value != null}
                onClear={props.onClear}
            >
                <NumberField value={props.value} positive={true} allowNull={true} onChange={props.onChange} />
            </ParameterField>
        </div>
    );
}

function ActivateByDefaultParameterField(props: { value: boolean; onChange(value: boolean): void }) {
    return (
        <div className="flex-fill">
            <ParameterField clearable={false} showClearIcon={false} noTitle={true}>
                <BooleanField
                    value={props.value}
                    title={'Activate By Default'}
                    allowNull={false}
                    onChange={props.onChange}
                />
            </ParameterField>
        </div>
    );
}

const EDITOR_CONFIG = {
    resize_enabled: false,
    allowedContent: true,
    disallowedContent: false
};

function HeaderTemplateTab(props: IHeaderTemplateTabProperties) {
    const { obj } = props;
    const headerTemplate = obj.value.options?.headerTemplate;

    return (
        <CKEditField
            editorConfig={EDITOR_CONFIG}
            value={headerTemplate}
            fillContainer={true}
            dialogOffsetTop={100}
            /* size={{
                    width: 500,
                    height: 400
                }}*/
            glob={props.glob}
            onChange={(value: string) => {
                props.onHeaderTemplateChange(obj, value);
            }}
            //onResize={props.onTemplateResize}
        />
    );
}

function getDialogContainer() {
    let dd = document.querySelector('div[role="dialog"]'); // className="modal fade show"

    if (dd != null) {
        let dd2 = dd.firstChild; // className="modal-dialog mw-100"

        if (dd2 != null) {
            const result = dd2.firstChild; // className="modal-content"

            if (result != null) {
                return result;
            }
        }
    }

    return null;
}
